'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { CheckIcon } from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@src/lib/utils';

const checkboxVariants = cva(
  'peer shrink-0 rounded-[4px] border shadow-xs transition-all outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 flex items-center justify-center',
  {
    variants: {
      variant: {
        primary:
          'border-[#C9C9C9] data-[state=checked]:bg-focus data-[state=checked]:text-white data-[state=checked]:border-focus focus-visible:border-ring focus-visible:ring-ring/50',
      },
      size: {
        sm: 'h-4 w-4',
        md: 'h-[18px] w-[18px]',
        lg: 'h-6 w-6',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  }
);

const indicatorVariants = cva(
  'flex items-center justify-center text-current transition-none',
  {
    variants: {
      size: {
        sm: '[&>svg]:h-3 [&>svg]:w-3',
        md: '[&>svg]:h-3.5 [&>svg]:w-3.5',
        lg: '[&>svg]:h-4 [&>svg]:w-4',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, variant, size, ...props }, ref) => {
  return (
    <CheckboxPrimitive.Root
      ref={ref}
      data-slot="checkbox"
      className={cn(checkboxVariants({ variant, size, className }))}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        data-slot="checkbox-indicator"
        className={cn(indicatorVariants({ size }))}
      >
        <CheckIcon />
      </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
});

Checkbox.displayName = 'Checkbox';

export { Checkbox, checkboxVariants, indicatorVariants };
